<script setup lang="ts">
import SelectCity from '@features/cities/components/CitiesBlock/SelectCity.vue'
import CitiesList from '@features/cities/components/CitiesList/CitiesList.vue'

import { DownloadApp } from '@features/apps'
import videoBackground from '@features/cities/assets/video.webm'
import { useCitiesBlock } from '@features/cities/composables/useCitiesBlock'
import { useResponsive } from '@shared/nuxt/useResponsive'

const { isMobile } = useResponsive()
const citiesBlock = ref<HTMLElement | null>(null)

const {
  searchQuery,
  isInputCityFocused,
  formattedCitiesList,
  detectedCity,
  isGeoLoading,
} = await useCitiesBlock()

const selectCityProps = computed(() => ({
  isInputFocused: isInputCityFocused.value,
  detectedCityName: detectedCity.value?.visibleName,
  detectedCityUrl: detectedCity.value?.cityUrl,
  isGeoLoading: isGeoLoading.value,
}))

const citiesListProps = computed(() => ({
  activeSearch: isInputCityFocused.value,
  searchQuery: searchQuery.value,
  formattedCitiesList: formattedCitiesList.value,
}))

const changeFocusCityInput = (isFocused: boolean) => {
  isInputCityFocused.value = isFocused
}

const isCitySelectionTarget = (target: EventTarget | null) =>
  target instanceof Node &&
  Boolean(
    citiesBlock.value?.querySelector('#searchInput')?.contains(target) ||
    citiesBlock.value?.querySelector('#citiesList')?.contains(target) ||
    citiesBlock.value?.querySelector('#btnOtherCity')?.contains(target),
  )

let pointerStartedInside = false

const onDocumentPointerDown = (event: PointerEvent) => {
  pointerStartedInside = isCitySelectionTarget(event.target)
}

const onDocumentClick = (event: MouseEvent) => {
  // Focus запускает анимацию и может сдвинуть input до mouseup.
  // Такой click по фону всё ещё относится к нажатию внутри поиска.
  const startedInside = event.detail > 0 && pointerStartedInside
  pointerStartedInside = false

  if (
    !(event.target instanceof Node) ||
    searchQuery.value.length ||
    !isInputCityFocused.value ||
    startedInside ||
    isCitySelectionTarget(event.target)
  ) {
    return
  }

  changeFocusCityInput(false)
}

// Закрываем по click, а не blur: ссылка города должна успеть обработать нажатие.
onMounted(() => {
  document.addEventListener('pointerdown', onDocumentPointerDown, true)
  document.addEventListener('click', onDocumentClick)
})
onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', onDocumentPointerDown, true)
  document.removeEventListener('click', onDocumentClick)
})
</script>

<template>
  <div
    ref="citiesBlock"
    class="cities-block"
    :class="{ 'cities-block--active-search': isInputCityFocused }"
  >
    <div v-if="!isMobile" class="cities-block__video">
      <transition name="video">
        <video v-show="!isInputCityFocused" autoplay muted loop>
          <source :src="videoBackground" type="video/mp4" />
        </video>
      </transition>

      <div class="cities-block__video-background"></div>
    </div>

    <div class="cities-block__content">
      <select-city
        v-model="searchQuery"
        v-bind="selectCityProps"
        @change-focus="changeFocusCityInput"
      />
      <download-app class="cities-block__download-application" />
    </div>

    <cities-list v-bind="citiesListProps" />
  </div>
</template>

<style scoped lang="scss">
.cities-block {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  min-height: unset;
  padding: 0 20px 148px 20px;
  margin-bottom: 40px;

  &__video {
    position: absolute;
    left: 0;
    width: 100vw;
    height: 100%;

    video {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .video-enter-active,
    .video-leave-active {
      transition: all 0.3s ease;
    }

    .video-enter-from,
    .video-leave-to {
      overflow: hidden;
      opacity: 0 !important;
    }

    .video-enter-to {
      overflow: hidden;
      opacity: 1 !important;
    }
  }

  &__video-background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(25, 26, 30, 0.6);
  }

  &__content {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding-top: 60px;
  }

  &__download-application {
    height: 56px;
    overflow: hidden;
    transition: 0.3s;
  }

  &--active-search {
    justify-content: flex-start;
    padding-bottom: 0;

    .cities-block__content {
      flex: unset;
    }

    .cities-block__download-application {
      height: 0;
      opacity: 0;
    }
  }

  @include media($bp-tablet) {
    min-height: 100vh;
    padding: 0 48px 0 48px;

    &__content {
      padding-top: 182px;
    }

    &__download-application {
      height: 224px;
    }
  }

  @include media($bp-desktop-mini) {
    padding: 0 0 75px 0;
    margin-bottom: 80px;

    &__video {
      left: calc(((100vw - 1000px) / -2));
    }

    &__content {
      padding-top: 224px;
    }
  }

  @include media($bp-desktop) {
    margin-bottom: 90px;

    &__video {
      left: calc(((100vw - 1200px) / -2));
    }

    &__content {
      flex-direction: row;
    }
  }
}
</style>
